/**
 * YouTube Lazy Load Facade
 * Delays loading of YouTube iframe until user interaction
 * Saves ~500KB+ and hundreds of ms per video
 *
 * Usage: Add data-youtube-id attribute to container
 */

class YouTubeLazyLoad {
  constructor() {
    this.init();
  }

  init() {
    // Find all deferred YouTube iframes
    const youtubeContainers = document.querySelectorAll('[data-youtube-lazy]');

    youtubeContainers.forEach(container => {
      const videoId = container.getAttribute('data-youtube-id');
      if (!videoId) return;

      // Get settings
      const autoplay = container.getAttribute('data-autoplay') === '1';
      const loop = container.getAttribute('data-loop') === '1';
      const controls = container.getAttribute('data-controls') === '1';
      const mute = container.getAttribute('data-mute') === '1';

      // Build iframe params
      let params = [];
      if (autoplay) params.push('autoplay=1');
      if (loop) params.push('loop=1', `playlist=${videoId}`);
      if (!controls) params.push('controls=0');
      if (mute) params.push('mute=1');
      params.push('enablejsapi=1', 'rel=0', 'modestbranding=1');

      const iframeSrc = `https://www.youtube-nocookie.com/embed/${videoId}?${params.join('&')}`;

      // Create play button overlay
      const playButton = container.querySelector('.youtube-lazy__play-button');
      if (playButton) {
        playButton.addEventListener('click', () => {
          this.loadVideo(container, iframeSrc);
        });
      }
    });
  }

  loadVideo(container, iframeSrc) {
    // Create iframe
    const iframe = document.createElement('iframe');
    iframe.setAttribute('src', iframeSrc);
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('loading', 'lazy');
    // Force GPU compositing to avoid non-composited animations
    iframe.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;transform:translateZ(0);will-change:transform;';

    // Replace container content with iframe
    container.innerHTML = '';
    container.appendChild(iframe);

    // Optional: track that video was loaded
    if (typeof window.dataLayer !== 'undefined') {
      window.dataLayer.push({
        event: 'youtube_video_loaded',
        video_id: container.getAttribute('data-youtube-id')
      });
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new YouTubeLazyLoad();
  });
} else {
  new YouTubeLazyLoad();
}

// Handle dynamic content (for sections loaded after page load)
if (typeof window.Shopify !== 'undefined' && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    new YouTubeLazyLoad();
  });
}
